<template>
  <div class="suspended-screen">
    <div class="suspended-background">
      <div class="suspended-overlay"></div>
    </div>

    <div :class="['suspended-content', { 'rtl': currentLanguage === 'ar' }]">
      <div class="suspended-icon">
        <i class="fas fa-lock"></i>
      </div>

      <h1 class="suspended-title">{{ msg.title }}</h1>

      <div class="suspended-reason">
        <span class="reason-label">{{ msg.subtitle }}</span>
      </div>

      <div class="suspended-message">
        <p>{{ msg.message }}</p>
      </div>

      <div class="suspended-notice">
        <div class="notice-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ msg.contact }}</span>
        </div>
        <p class="notice-action">{{ msg.action }}</p>
      </div>

      <div class="suspended-footer">
        <i class="fas fa-shield-alt"></i>
        <span>Table Tech Order Display</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SuspendedScreen',
  props: {
    currentLanguage: {
      type: String,
      default: 'de'
    }
  },
  computed: {
    messages() {
      return {
        de: {
          title: "System Gesperrt",
          subtitle: "Zahlung fällig",
          message: "Das System wurde aufgrund ausstehender Zahlungen gesperrt.",
          contact: "Bitte kontaktieren Sie nicht den Systembesitzer.",
          action: "Kontaktieren Sie Ihren Restaurantbesitzer oder Ihr Buchhaltungsteam, um das System wieder zu aktivieren."
        },
        ar: {
          title: "النظام معلق",
          subtitle: "Zahlung fällig",
          message: "تم تعليق النظام بسبب الدفعات المستحقة.",
          contact: "الرجاء عدم الاتصال بمالك النظام.",
          action: "اتصل بمالك المطعم أو فريق المحاسبة الخاص بك لإعادة تنشيط النظام."
        }
      };
    },
    msg() {
      return this.messages[this.currentLanguage] || this.messages.de;
    }
  }
};
</script>

<style scoped>
.suspended-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999999;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.suspended-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  animation: gradientShift 10s ease infinite;
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.suspended-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(220, 53, 69, 0.03) 10px,
      rgba(220, 53, 69, 0.03) 20px
    );
  animation: diagonalMove 20s linear infinite;
}

@keyframes diagonalMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
}

.suspended-content {
  position: relative;
  z-index: 10;
  max-width: 700px;
  width: 90%;
  background: var(--ods-surface-3);
  border-radius: var(--ods-radius-lg);
  padding: 60px 40px;
  box-shadow: 0 50px 100px rgba(0, 0, 0, 0.5),
    0 0 100px rgba(220, 53, 69, 0.2);
  text-align: center;
  animation: slideIn 0.6s ease-out;
  border: 3px solid #dc3545;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.suspended-content.rtl {
  direction: rtl;
  font-family: inherit;
}

.suspended-icon {
  font-size: 120px;
  color: #dc3545;
  margin-bottom: 30px;
  animation: lockPulse 2s ease-in-out infinite;
}

@keyframes lockPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.suspended-title {
  font-size: 48px;
  font-weight: 800;
  color: #dc3545;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.suspended-reason {
  display: inline-block;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: var(--ods-ink);
  padding: 15px 40px;
  border-radius: var(--ods-radius-lg);
  margin-bottom: 40px;
  font-size: 24px;
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(220, 53, 69, 0.4);
}

.suspended-message {
  margin-bottom: 40px;
}

.suspended-message p {
  font-size: 22px;
  color: #2c3e50;
  line-height: 1.6;
  font-weight: 500;
}

.suspended-notice {
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: var(--ods-radius-lg);
  padding: 30px;
  margin-bottom: 40px;
}

.notice-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 20px;
  color: #856404;
  font-weight: 700;
  margin-bottom: 20px;
}

.notice-warning i {
  font-size: 28px;
}

.notice-action {
  font-size: 18px;
  color: #856404;
  line-height: 1.8;
  margin: 0;
  font-weight: 600;
}

.suspended-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 16px;
  color: var(--ods-ink-3);
  padding-top: 30px;
  border-top: 2px solid #e9ecef;
  font-weight: 600;
}

.suspended-footer i {
  font-size: 20px;
  color: #dc3545;
}

/* RTL Adjustments */
.suspended-content.rtl .notice-warning {
  flex-direction: row-reverse;
}
</style>
